import { useEffect, useMemo, useState } from "react";
import { telLink, whatsappLink } from "@chbapark/domain/normalization";
import { Link } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  greeting,
  todayKey,
  useHandledRecommendations,
  useMyProfile,
  useOpenTasks,
  useRecommendations,
  type Recommendation,
} from "../data";
import {
  DISMISS_REASONS,
  OWNERS,
  actOnRecommendation,
  computeRecommendationsNow,
  extendTodayList,
} from "../actions";
import { messageFor, useMessageTemplates } from "../messages";
import {
  IcAlert,
  IcArchive,
  IcCalendar,
  IcCheck,
  IcChevron,
  IcClock,
  IcForm,
  IcPeople,
  IcPhone,
  IcSearch,
  IcSpark,
  IcWallet,
  IcWhatsapp,
} from "../ui/icons";

/**
 * Today (spec §4.1): **עם מי כדאי ליצור קשר היום, ולמה?**
 *
 * ## The shape, and why it changed (2026-09-23)
 *
 * Each card used to be a tall panel with an avatar, three lines of text and six
 * round buttons of equal weight — two people filled the screen and nothing said
 * which button was the point. The product owner's verdict: "המרווחים ענקיים,
 * המון כפתורים, אין שום עדיפות לכפתור אחד על משנהו".
 *
 * Now a card is a row. The **reason** leads, in the colour of its kind — a
 * birthday does not look like a yahrzeit or a donation. There is **one primary
 * action**, and it follows the state: WhatsApp (or a call, where the rule says a
 * call is right) until contact has been made, then "בוצע". Everything a person
 * does less often — later, not relevant, hand to the other owner — is behind ⋯.
 *
 * A card about a gathering is a different kind of thing and is drawn as one: its
 * action is "mark who came", and "לא התקיים" sits next to it, because a class
 * that did not meet (a holiday) had no honest way off the screen.
 *
 * ## What it still deliberately does not do
 *
 * No count of everything left undone, and no carrying yesterday's cards
 * forward — §4.1 forbids the backlog. But the day's capacity is no longer a
 * hard seven: "עוד משימות" asks the engine for more, for a day spent working
 * through the list.
 */
export function TodayPage() {
  const profile = useMyProfile();
  const myOwner = profile?.ownerSlug ?? null;
  const { templates } = useMessageTemplates();

  const [tab, setTab] = useState<"mine" | "other" | "all">("mine");
  const [showHandled, setShowHandled] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [extending, setExtending] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  /**
   * Cards archived from this screen, hidden before the server answers, and the
   * one that can still be undone.
   *
   * Archive is a single tap with no confirmation, so it needs an undo — the
   * bar at the bottom, for a few seconds. Undo waits for the archive itself to
   * land before restoring, so a quick tap cannot race it.
   */
  const [archived, setArchived] = useState<ReadonlySet<string>>(new Set());
  const [undo, setUndo] = useState<{ id: string; name: string; pending: Promise<unknown> } | null>(null);
  const unarchive = (id: string) =>
    setArchived((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });

  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo((u) => (u === undo ? null : u)), 7000);
    return () => clearTimeout(t);
  }, [undo]);

  function archive(rec: Recommendation) {
    setNote(null);
    setArchived((s) => new Set(s).add(rec.id));
    const pending = actOnRecommendation({ recommendationId: rec.id, action: "archive" });
    setUndo({ id: rec.id, name: rec.personName || rec.eventTitle || "הכרטיס", pending });
    pending.catch((e) => {
      unarchive(rec.id);
      setUndo((u) => (u?.id === rec.id ? null : u));
      setNote(e instanceof Error ? e.message : "לא הצלחנו להעביר לארכיון, אז החזרנו את הכרטיס. נסו שוב.");
    });
  }

  async function restore(id: string, pending?: Promise<unknown>) {
    setUndo(null);
    setNote(null);
    try {
      if (pending) await pending;
      await actOnRecommendation({ recommendationId: id, action: "restore" });
      unarchive(id);
    } catch (e) {
      setNote(e instanceof Error ? e.message : "לא הצלחנו להחזיר את הכרטיס. נסו שוב.");
    }
  }

  const otherOwner = myOwner === "mendi" ? "shira" : myOwner === "shira" ? "mendi" : null;
  const viewing = tab === "all" || !myOwner ? null : tab === "mine" ? myOwner : otherOwner;

  // Every owner's cards, split here: the tabs show a count each, so all three
  // lists have to be known at once.
  const { data, loading, error } = useRecommendations(null);
  const handled = useHandledRecommendations(viewing);
  const openTasks = useOpenTasks(60);

  const me = OWNERS.find((o) => o.slug === myOwner);

  /**
   * One card per person, even in the "everything" tab.
   *
   * A person owned by both מענדי and שירה gets a card on each list, which is
   * right — they both know them. The combined view collapses the pair and keeps
   * the viewer's own copy, so acting on it closes their card.
   */
  const lists = useMemo(() => {
    const sorted = data.filter((r) => !archived.has(r.id)).sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
    const seen = new Map<string, Recommendation>();
    for (const r of sorted) {
      const key = `${r.personId ?? r.eventId ?? r.id}|${r.ruleId ?? ""}`;
      const existing = seen.get(key);
      if (!existing || (myOwner && r.assignedTo === myOwner)) seen.set(key, r);
    }
    return {
      mine: sorted.filter((r) => r.assignedTo === myOwner),
      other: sorted.filter((r) => r.assignedTo === otherOwner),
      all: [...seen.values()].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99)),
    };
  }, [data, archived, myOwner, otherOwner]);

  const cards = myOwner ? lists[tab] : lists.all;

  /** Tasks due later than today — context, so a quiet day does not look empty. */
  const upcoming = useMemo(
    () =>
      openTasks.items
        .filter((t) => t.at !== null && !t.overdue)
        .filter((t) => (t.personId ? !cards.some((c) => c.personId === t.personId) : true))
        .slice(0, 3),
    [openTasks.items, cards],
  );

  async function refresh() {
    setRefreshing(true);
    setNote(null);
    try {
      const r = await computeRecommendationsNow();
      const total = Object.values(r.perOwner).reduce((a, b) => a + b, 0);
      setNote(
        total === 0
          ? `אין היום אף אחד להציע. בדקנו ${Object.values(r.signalCounts).reduce((a, b) => a + b, 0)} סימנים.`
          : `עדכנו את הרשימה. ${r.created} המלצות חדשות, ${r.keptFromEarlierRun} נשארו כמו שהיו.`,
      );
    } catch (e) {
      setNote(e instanceof Error ? e.message : "לא הצלחנו לחשב את הרשימה. נסו שוב.");
    } finally {
      setRefreshing(false);
    }
  }

  /** "עוד משימות" — for the list on screen, or the viewer's own on "הכול". */
  const extendFor = viewing ?? myOwner;
  async function more() {
    if (!extendFor) return;
    setExtending(true);
    setNote(null);
    try {
      const r = await extendTodayList(extendFor);
      setNote(
        r.atCeiling
          ? "הגעתם למקסימום להיום. מחר נתחיל רשימה חדשה."
          : r.added === 0
            ? "אין כרגע עוד המלצות להיום. כל מה שיש כבר על המסך."
            : r.added === 1
              ? "הוספנו משימה אחת."
              : `הוספנו ${r.added} משימות.`,
      );
    } catch (e) {
      setNote(e instanceof Error ? e.message : "לא הצלחנו להוסיף. נסו שוב.");
    } finally {
      setExtending(false);
    }
  }

  const TABS: { id: typeof tab; label: string; n: number }[] = myOwner
    ? [
        { id: "mine", label: "שלי", n: lists.mine.length },
        {
          id: "other",
          label: OWNERS.find((o) => o.slug === otherOwner)?.label ?? "השני",
          n: lists.other.length,
        },
        { id: "all", label: "הכול", n: lists.all.length },
      ]
    : [];

  return (
    <div className="screen">
      <header className="hdr today-hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>
              {greeting()}
              {me && `, ${me.greetingName}`}
            </h1>
            <p className="sub">{loading ? "טוענים…" : summarize(cards)}</p>
          </div>
          <div className="hdr-actions">
            <Link to="/people" className="icon-btn" aria-label="חיפוש">
              <IcSearch />
            </Link>
          </div>
        </div>

        {TABS.length > 0 && (
          <div className="today-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={tab === t.id ? "on" : undefined}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                <span className="today-tab-n">{t.n}</span>
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="body">
        {!myOwner && profile && <WhoAmI uid={profile.id} />}

        {note && <div className="notice info">{note}</div>}

        {error && (
          <div className="notice error">
            לא הצלחנו לטעון את ההמלצות. זו תקלה, לא סימן שאין מה לעשות. נסו לרענן את הדף.
            <br />
            <span className="faint faint-xs mono">{error}</span>
          </div>
        )}

        {loading ? (
          <LoadingRows />
        ) : cards.length === 0 ? (
          <EmptyToday onRefresh={refresh} refreshing={refreshing} upcoming={upcoming} />
        ) : (
          <>
            <div className="today-list">
              {cards.map((rec) =>
                rec.action === "rollcall" && rec.eventId ? (
                  <EventRow key={rec.id} rec={rec} />
                ) : (
                  <PersonRow
                    key={rec.id}
                    rec={rec}
                    otherOwner={
                      rec.assignedTo === "mendi" ? "shira" : rec.assignedTo === "shira" ? "mendi" : otherOwner
                    }
                    message={messageFor(templates, rec, rec.assignedTo ?? myOwner)}
                    onArchive={() => archive(rec)}
                  />
                ),
              )}
            </div>

            {extendFor && (
              <button className="btn btn-wide today-more" onClick={more} disabled={extending}>
                {extending ? "מחפשים עוד…" : "עוד משימות להיום"}
              </button>
            )}
          </>
        )}

        {upcoming.length > 0 && cards.length > 0 && <Upcoming items={upcoming} />}

        {handled.data.length > 0 && (
          <section className="section">
            <button className="today-link" onClick={() => setShowHandled((v) => !v)}>
              {showHandled ? "הסתרה" : `מה שטיפלתם היום (${handled.data.length})`}
            </button>
            {showHandled && (
              <div className="card" style={{ marginTop: 8 }}>
                {handled.data.map((r, i) => (
                  <div
                    className="list-row"
                    key={r.id}
                    style={{ borderBottom: i === handled.data.length - 1 ? "0" : undefined }}
                  >
                    <span className="grow">
                      {r.personName}
                      <span className="faint" style={{ display: "block", marginTop: 2 }}>
                        {r.reasonText} · {statusLabel(r)}
                      </span>
                    </span>
                    {r.dismissReason === "archived" && (
                      <button className="btn btn-sm" onClick={() => void restore(r.id)}>
                        החזירו
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {cards.length > 0 && (
          <button className="today-link" onClick={refresh} disabled={refreshing}>
            {refreshing ? "מחשבים…" : "חשבו את הרשימה מחדש"}
          </button>
        )}
      </div>

      {undo && (
        <div className="undo-bar" role="status">
          <span className="grow">{undo.name} הועבר לארכיון</span>
          <button className="undo-btn" onClick={() => void restore(undo.id, undo.pending)}>
            ביטול
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * What the day holds, counted by kind.
 *
 * It said "2 אנשים שכדאי ליצור איתם קשר" over one person and one class, because
 * it counted cards and called them all people.
 */
function summarize(cards: Recommendation[]): string {
  if (cards.length === 0) return "אין מה לעשות עכשיו";
  const events = cards.filter((c) => c.action === "rollcall" && c.eventId).length;
  const people = cards.length - events;
  const parts: string[] = [];
  if (people > 0) parts.push(people === 1 ? "איש קשר אחד" : `${people} אנשי קשר`);
  if (events > 0) parts.push(events === 1 ? "אירוע אחד לסימון נוכחות" : `${events} אירועים לסימון נוכחות`);
  if (parts.length === 1) return `להיום: ${parts[0]}`;
  const second = parts[1]!;
  return `להיום: ${parts[0]} ${/^\d/.test(second) ? "ו־" : "ו"}${second}`;
}

/**
 * Which owner this account is.
 *
 * Asked once, on the screen that needs the answer, rather than buried in
 * settings — the tabs cannot say "mine" until it is answered.
 */
function WhoAmI({ uid }: { uid: string }) {
  const [busy, setBusy] = useState(false);

  async function pick(slug: string) {
    setBusy(true);
    try {
      await updateDoc(doc(db, "users", uid), { ownerSlug: slug });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-highlight">
      <div className="card-body">
        <p className="name-row">מי משתמש בחשבון הזה?</p>
        <p className="muted">
          אנשי הקשר מחולקים בין מענדי לשירה. בחרו פעם אחת, כדי שהמסך ידע מה „שלי”.
        </p>
        <div className="chips" style={{ marginTop: 10 }}>
          {OWNERS.map((o) => (
            <button key={o.slug} className="tagchip" onClick={() => pick(o.slug)} disabled={busy}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The kind of a card: its colour and its icon. One entry per rule. */
const KIND: Record<string, { kind: string; icon: React.ReactNode }> = {
  donation_unthanked: { kind: "kind-amber", icon: <IcWallet size={15} /> },
  thank_you_due: { kind: "kind-amber", icon: <IcWallet size={15} /> },
  birthday_upcoming: { kind: "kind-green", icon: <IcSpark size={14} /> },
  yahrzeit_upcoming: { kind: "kind-slate", icon: <IcCalendar size={15} /> },
  follow_up_due: { kind: "kind-orange", icon: <IcClock size={15} /> },
  enquiry_unanswered: { kind: "kind-orange", icon: <IcForm size={15} /> },
  registration_unwelcomed: { kind: "kind-green", icon: <IcPeople size={15} /> },
  event_confirm: { kind: "kind-green", icon: <IcCalendar size={15} /> },
  event_regular_reminder: { kind: "kind-orange", icon: <IcCalendar size={15} /> },
  event_attendance_unmarked: { kind: "kind-green", icon: <IcPeople size={15} /> },
  service_request_open: { kind: "kind-red", icon: <IcAlert size={15} /> },
  significant_no_contact: { kind: "kind-slate", icon: <IcPhone size={15} /> },
};
const KIND_FALLBACK = { kind: "kind-slate", icon: <IcPhone size={15} /> };

/**
 * The shared state and the calls every card makes.
 *
 * A card leaves on the tap, not on the reply: a cold function in me-west1
 * takes ~2.4s to answer, and the listener would remove the card anyway once the
 * server wrote. A failed write puts it back with the error.
 */
function useCardActions(rec: Recommendation) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cleared, setCleared] = useState(false);

  async function act(input: Parameters<typeof actOnRecommendation>[0]) {
    const leaves = input.action !== "contacted";
    setBusy(true);
    setErr(null);
    if (leaves) setCleared(true);
    try {
      await actOnRecommendation(input);
    } catch (e) {
      setCleared(false);
      setErr(e instanceof Error ? e.message : "לא הצלחנו לבצע את הפעולה. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  const later = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(8, 0, 0, 0);
    return act({ recommendationId: rec.id, action: "later", laterDate: d.toISOString() });
  };

  return { busy, err, cleared, act, later };
}

function LaterChips({ onPick, busy }: { onPick: (days: number) => void; busy: boolean }) {
  return (
    <div className="chips">
      {(
        [
          ["מחר", 1],
          ["בעוד 3 ימים", 3],
          ["בעוד שבוע", 7],
          ["בעוד חודש", 30],
        ] as const
      ).map(([label, days]) => (
        <button key={label} className="tagchip" disabled={busy} onClick={() => onPick(days)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function PersonRow({
  rec,
  otherOwner,
  message,
  onArchive,
}: {
  rec: Recommendation;
  otherOwner: string | null;
  message: string | null;
  /** One tap off the screen, and not back tomorrow. See `archive` above. */
  onArchive: () => void;
}) {
  const { busy, err, cleared, act, later } = useCardActions(rec);
  const [panel, setPanel] = useState<"none" | "menu" | "later" | "not_relevant" | "reassign">("none");
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  const name = rec.personName || "בלי שם";
  const k = KIND[rec.ruleId ?? ""] ?? KIND_FALLBACK;
  const started = rec.status === "in_progress";
  const callFirst = rec.channel === "phone";

  /**
   * Normalised, not merely stripped of punctuation: a number typed as
   * 050-866-6333 has no country code, and WhatsApp refuses it. `whatsappLink`
   * returns null where there is nothing worth linking to, so the button is
   * absent rather than dead. The message is the card's own, from the templates.
   */
  const waHref = whatsappLink(rec.personPhone, message ?? undefined);
  const telHref = telLink(rec.personPhone);
  const personHref = rec.personId ? `/people/${rec.personId}` : "#";
  const done = () =>
    act({ recommendationId: rec.id, action: "done", channel: rec.channel === "whatsapp" ? "whatsapp" : "phone" });

  if (cleared) return null;

  const whatsappBtn = (primary: boolean) =>
    waHref && (
      <a
        className={primary ? "act-btn act-wa" : "act-btn"}
        href={waHref}
        target="_blank"
        rel="noreferrer"
        aria-label="וואטסאפ"
        onClick={() => act({ recommendationId: rec.id, action: "contacted", channel: "whatsapp" })}
      >
        <IcWhatsapp size={primary ? 20 : 18} />
      </a>
    );
  const callBtn = (primary: boolean) =>
    telHref && (
      <a
        className={primary ? "act-btn act-call" : "act-btn"}
        href={telHref}
        aria-label="התקשרו"
        onClick={() => act({ recommendationId: rec.id, action: "contacted", channel: "phone" })}
      >
        <IcPhone size={primary ? 19 : 17} />
      </a>
    );

  return (
    <article className={`today-row ${k.kind}`}>
      <div className="today-main">
        <Link to={personHref} className="today-body">
          <span className="today-name">{name}</span>
          <span className="today-reason">
            {k.icon}
            <span>
              {rec.reasonText}
              {rec.metaText && <span className="today-when"> · {rec.metaText}</span>}
            </span>
          </span>
          {(rec.alsoBecause ?? []).length > 0 && (
            <span className="today-also">וגם: {(rec.alsoBecause ?? []).join(" · ")}</span>
          )}
          {started && <span className="today-also">פתחתם שיחה. דיברתם? לחצו ✓</span>}
          {rec.reassignedFrom && (
            <span className="today-also">
              הועבר אליכם מ{OWNERS.find((o) => o.slug === rec.reassignedFrom)?.label ?? rec.reassignedFrom}
            </span>
          )}
        </Link>

        <div className="today-acts">
          {started ? (
            <button className="act-btn act-done" disabled={busy} onClick={done} aria-label="בוצע">
              <IcCheck size={20} />
            </button>
          ) : callFirst ? (
            callBtn(true) ?? whatsappBtn(true)
          ) : (
            whatsappBtn(true) ?? callBtn(true)
          )}
          {started
            ? callFirst
              ? callBtn(false)
              : whatsappBtn(false)
            : callFirst
              ? telHref && whatsappBtn(false)
              : waHref && callBtn(false)}
          {/* Its own button, not inside ⋯: putting a card away took four or five
              taps through "not relevant" and a reason, for cards — a kapparot
              thank-you — nobody meant to act on. */}
          <button
            className="act-btn act-archive"
            aria-label="לארכיון"
            title="לארכיון"
            disabled={busy}
            onClick={onArchive}
          >
            <IcArchive size={18} />
          </button>
          <button
            className="act-btn act-more"
            aria-label="עוד פעולות"
            aria-expanded={panel !== "none"}
            onClick={() => setPanel((p) => (p === "none" ? "menu" : "none"))}
          >
            ⋯
          </button>
        </div>
      </div>

      {panel === "menu" && (
        <div className="today-menu">
          {!started && (
            <button className="tagchip" disabled={busy} onClick={done}>
              ✓ כבר דיברנו
            </button>
          )}
          <button className="tagchip" disabled={busy} onClick={() => setPanel("later")}>
            אחר כך
          </button>
          <button className="tagchip" disabled={busy} onClick={() => setPanel("not_relevant")}>
            לא רלוונטי
          </button>
          {otherOwner && (
            <button className="tagchip" disabled={busy} onClick={() => setPanel("reassign")}>
              להעביר ל{OWNERS.find((o) => o.slug === otherOwner)?.label ?? "השני"}
            </button>
          )}
          {rec.personId && (
            <Link className="tagchip" to={personHref}>
              לכרטיס איש הקשר
            </Link>
          )}
        </div>
      )}

      {panel === "later" && (
        <div className="today-menu today-menu-col">
          <p className="faint">מתי לחזור לזה? נוסיף משימה לתאריך הזה.</p>
          <LaterChips busy={busy} onPick={(d) => void later(d)} />
          <button className="today-link" onClick={() => setPanel("menu")}>
            חזרה
          </button>
        </div>
      )}

      {panel === "not_relevant" && (
        <div className="today-menu today-menu-col">
          <p className="faint">למה זה לא רלוונטי?</p>
          <div className="chips">
            {DISMISS_REASONS.map((r) => (
              <button
                key={r.id}
                className={reason === r.id ? "tagchip tagchip-on" : "tagchip"}
                onClick={() => setReason(r.id)}
                disabled={busy}
              >
                {r.label}
              </button>
            ))}
          </div>
          {reason !== "" && <p className="faint">{DISMISS_REASONS.find((r) => r.id === reason)?.hint}</p>}
          <input
            className="form-input"
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            placeholder="הערה (לא חובה)"
            disabled={busy}
          />
          <div className="btn-row">
            <button
              className="btn btn-primary btn-sm"
              disabled={busy || reason === ""}
              onClick={() =>
                act({
                  recommendationId: rec.id,
                  action: "not_relevant",
                  reason,
                  note: reasonNote.trim() || undefined,
                })
              }
            >
              שמרו
            </button>
            <button className="btn btn-sm" onClick={() => setPanel("menu")} disabled={busy}>
              חזרה
            </button>
          </div>
        </div>
      )}

      {panel === "reassign" && (
        <div className="today-menu today-menu-col">
          <p className="faint">גם הכרטיס וגם איש הקשר יעברו. בלי זה, הכרטיס יחזור אליכם מחר.</p>
          <div className="btn-row">
            <button
              className="btn btn-primary btn-sm"
              disabled={busy || !otherOwner}
              onClick={() =>
                act({ recommendationId: rec.id, action: "reassign", assignTo: otherOwner ?? undefined })
              }
            >
              להעביר ל{OWNERS.find((o) => o.slug === otherOwner)?.label ?? "השני"}
            </button>
            <button className="btn btn-sm" onClick={() => setPanel("menu")} disabled={busy}>
              חזרה
            </button>
          </div>
        </div>
      )}

      {err && <div className="notice error">{err}</div>}
    </article>
  );
}

/**
 * A gathering, not a person.
 *
 * Its action is "mark who came". "לא התקיים" stands next to it, not behind a
 * menu: a class that did not meet — a holiday — used to have no honest way off
 * the screen, only "done", which would have said it took place. The server
 * cancels that occurrence, so the roster does not learn that nobody came.
 */
function EventRow({ rec }: { rec: Recommendation }) {
  const { busy, err, cleared, act, later } = useCardActions(rec);
  const [panel, setPanel] = useState<"none" | "later">("none");
  const title = rec.eventTitle || rec.personName || "אירוע";
  const href = `/rollcall?event=${rec.eventId}`;

  if (cleared) return null;

  return (
    <article className="today-row today-event kind-green">
      <div className="today-main">
        <Link to={href} className="today-event-ico rail-ico tint-green" aria-hidden="true" tabIndex={-1}>
          <IcPeople size={18} />
        </Link>
        <Link to={href} className="today-body">
          <span className="today-name">{title}</span>
          <span className="today-reason">
            <span>
              מי השתתף?{rec.metaText && <span className="today-when"> · {rec.metaText}</span>}
            </span>
          </span>
        </Link>
        <div className="today-acts">
          <Link className="btn btn-primary btn-sm today-event-go" to={href}>
            סמנו משתתפים
          </Link>
        </div>
      </div>

      {panel === "none" ? (
        <div className="today-quiet">
          <button
            className="today-link"
            disabled={busy}
            onClick={() => {
              if (confirm(`לסמן ש„${title}” לא התקיים? המפגש יסומן כמבוטל, והכרטיס ייסגר.`))
                void act({ recommendationId: rec.id, action: "did_not_happen" });
            }}
          >
            לא התקיים
          </button>
          <span className="faint">·</span>
          <button className="today-link" disabled={busy} onClick={() => setPanel("later")}>
            אחר כך
          </button>
          <span className="faint">·</span>
          <button
            className="today-link"
            disabled={busy}
            onClick={() => act({ recommendationId: rec.id, action: "done" })}
          >
            כבר סימנו
          </button>
        </div>
      ) : (
        <div className="today-menu today-menu-col">
          <LaterChips busy={busy} onPick={(d) => void later(d)} />
          <button className="today-link" onClick={() => setPanel("none")}>
            חזרה
          </button>
        </div>
      )}

      {err && <div className="notice error">{err}</div>}
    </article>
  );
}

function Upcoming({ items }: { items: { id: string; title: string; at: string | null; personId: string | null }[] }) {
  return (
    <section className="section">
      <div className="section-head">
        <div className="section-ico tint-slate">
          <IcClock size={17} />
        </div>
        <div className="section-title">משימות לימים הבאים</div>
      </div>
      <div className="card">
        {items.map((t, i) => (
          <Link
            key={t.id}
            to={t.personId ? `/people/${t.personId}` : "/tasks"}
            className="list-row"
            style={{ borderBottom: i === items.length - 1 ? "0" : undefined }}
          >
            <span className="grow">
              {t.title}
              <span className="faint" style={{ display: "block", marginTop: 2 }}>
                {t.at ? new Date(t.at).toLocaleDateString("he-IL", { day: "numeric", month: "long" }) : ""}
              </span>
            </span>
            <IcChevron size={15} />
          </Link>
        ))}
      </div>
    </section>
  );
}

function statusLabel(r: Recommendation): string {
  if (r.status === "done") return "בוצע";
  if (r.status === "snoozed") return "נדחה";
  if (r.status === "reassigned") return "הועבר";
  if (r.status === "dismissed") {
    if (r.dismissReason === "did_not_happen") return "לא התקיים";
    if (r.dismissReason === "archived") return "בארכיון";
    const reason = DISMISS_REASONS.find((x) => x.id === r.dismissReason);
    return `לא רלוונטי${reason ? ` · ${reason.label}` : ""}`;
  }
  return r.status ?? "";
}

function LoadingRows() {
  return (
    <div className="today-list">
      {[0, 1, 2].map((i) => (
        <div key={i} className="today-row">
          <div className="today-main">
            <div className="grow" style={{ display: "grid", gap: 8 }}>
              <div className="skeleton" style={{ height: 14, width: "45%" }} />
              <div className="skeleton" style={{ height: 12, width: "70%" }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * An empty Today is a legitimate answer, not a failure.
 *
 * The engine only fires on positive signals — a form, a donation, a yahrzeit, a
 * reminder somebody set. Saying so plainly is better than inventing work; what
 * it must not do is look broken, so it explains what it watches.
 */
function EmptyToday({
  onRefresh,
  refreshing,
  upcoming,
}: {
  onRefresh: () => void;
  refreshing: boolean;
  upcoming: { id: string; title: string; at: string | null; personId: string | null }[];
}) {
  return (
    <>
      <div className="empty">
        <div className="empty-ico tint-green">
          <IcCheck size={26} />
        </div>
        <h3>אין מה לעשות עכשיו</h3>
        <p>
          המסך הזה מציע אנשי קשר רק כשמשהו קרה: פנייה מהאתר, תרומה, הרשמה, יארצייט או יום
          הולדת שמתקרבים, או משימה שקבעתם בעצמכם.
        </p>
      </div>

      {upcoming.length > 0 && <Upcoming items={upcoming} />}

      <button className="btn btn-wide" onClick={onRefresh} disabled={refreshing}>
        {refreshing ? "מחשבים…" : "בדקו שוב"}
      </button>
      <p className="faint" style={{ padding: "8px 6px 0", textAlign: "center" }}>
        הרשימה נבנית כל בוקר ב־06:00. {todayKey()}
      </p>
    </>
  );
}
